// Database entity types for the expense tracking app

export interface Couple {
  id: string;
  partner1_name: string;
  partner2_name: string;
  created_at: string;
  total_points: number;
  partner1_points: number;
  partner2_points: number;
}

export interface Category {
  id: string;
  couple_id: string;
  name: string;
  icon: string;
  color: string;
  is_active: boolean;
  usage_count: number;
}

export interface Expense {
  id: string;
  couple_id: string;
  category_id: string;
  amount: number;
  description?: string;
  expense_date: string;
  added_by: string;
  created_at: string;
}

export interface Challenge {
  id: string;
  couple_id: string;
  name: string;
  description: string;
  challenge_type: ChallengeType;
  target_amount?: number;
  target_days?: number;
  points_reward: number;
  start_date: string;
  end_date: string;
  status: ChallengeStatus;
  current_progress: number;
}

export interface Achievement {
  id: string;
  couple_id: string;
  name: string;
  description: string;
  icon: string;
  points_awarded: number;
  unlocked_at: string;
  unlocked_by: string;
}

// Enums for type safety
export enum ChallengeType {
  SPENDING_LIMIT = 'spending_limit',
  SAVINGS_GOAL = 'savings_goal',
  CATEGORY_LIMIT = 'category_limit',
  STREAK_GOAL = 'streak_goal',
  NO_SPEND_DAYS = 'no_spend_days'
}

export enum ChallengeStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  FAILED = 'failed',
  PAUSED = 'paused'
}

export enum Partner {
  PARTNER1 = 'partner1',
  PARTNER2 = 'partner2'
}

// Create types for database operations
export interface CreateCoupleRequest {
  partner1_name: string;
  partner2_name: string;
}

export interface CreateCategoryRequest {
  couple_id: string;
  name: string;
  icon: string;
  color: string;
}

export interface CreateExpenseRequest {
  couple_id: string;
  category_id: string;
  amount: number;
  description?: string;
  expense_date: string;
  added_by: Partner;
}

export interface CreateChallengeRequest {
  couple_id: string;
  name: string;
  description: string;
  challenge_type: ChallengeType;
  target_amount?: number;
  target_days?: number;
  points_reward: number;
  start_date: string;
  end_date: string;
}

export interface CreateAchievementRequest {
  couple_id: string;
  name: string;
  description: string;
  icon: string;
  points_awarded: number;
  unlocked_by: Partner;
}

// Response types for API operations
export interface DatabaseResponse<T> {
  data: T | null;
  error: string | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  count: number;
  error: string | null;
}

// Aggregate types for dashboard and insights
export interface ExpenseSummary {
  total_amount: number;
  expense_count: number;
  category_breakdown: CategorySummary[];
  partner1_total: number;
  partner2_total: number;
}

export interface CategorySummary {
  category_id: string;
  category_name: string;
  category_color: string;
  category_icon: string;
  total_amount: number;
  expense_count: number;
  percentage: number;
}

export interface MonthlySpending {
  month: string;
  year: number;
  total_amount: number;
  partner1_amount: number;
  partner2_amount: number;
}

export interface ChallengeProgress {
  challenge: Challenge;
  progress_percentage: number;
  days_remaining: number;
  is_on_track: boolean;
}

// Default categories that should be created for new couples
export interface DefaultCategory {
  name: string;
  icon: string;
  color: string;
}

export const DEFAULT_CATEGORIES: DefaultCategory[] = [
  { name: 'Food & Dining', icon: 'utensils', color: '#ef4444' },
  { name: 'Groceries', icon: 'shopping-cart', color: '#22c55e' },
  { name: 'Transportation', icon: 'car', color: '#3b82f6' },
  { name: 'Entertainment', icon: 'film', color: '#a855f7' },
  { name: 'Shopping', icon: 'shopping-bag', color: '#f59e0b' },
  { name: 'Bills & Utilities', icon: 'receipt', color: '#ef4444' },
  { name: 'Healthcare', icon: 'heart', color: '#ec4899' },
  { name: 'Travel', icon: 'plane', color: '#06b6d4' },
  { name: 'Personal Care', icon: 'user', color: '#84cc16' },
  { name: 'Other', icon: 'more-horizontal', color: '#6b7280' }
];